import React, { useState } from 'react';
import type {
  CatalogAIPolicy,
  CatalogDirection,
  CatalogPreliminary,
  CatalogQuestion,
  CatalogService,
} from '../types';
import { PreliminaryEditModal } from './PreliminaryEditModal';
import { QuestionEditDrawer } from './QuestionEditDrawer';

interface CoverageReviewQuestionsTabProps {
  directions: CatalogDirection[];
  services: CatalogService[];
  questions: CatalogQuestion[];
  preliminaries: CatalogPreliminary[];
  aiPolicy: CatalogAIPolicy;
  initialSelectedServiceId?: string;
  onUpdateQuestion: (updated: Partial<CatalogQuestion>) => void;
  onToggleQuestionApproval: (questionId: string, currentStatus: string) => void;
  onDeleteQuestion: (questionId: string) => void;
  onRestoreQuestion: (questionId: string) => void;
  onUpdatePreliminary: (updated: Partial<CatalogPreliminary>) => void;
  onTogglePreliminaryApproval: (questionId: string, currentStatus: string) => void;
  onUpdateAIPolicy: (policy: CatalogAIPolicy) => void;
  onNavigateToRoleAccess: (serviceId: string) => void;
}

export const CoverageReviewQuestionsTab: React.FC<CoverageReviewQuestionsTabProps> = ({
  directions,
  services,
  questions,
  preliminaries,
  aiPolicy,
  initialSelectedServiceId,
  onUpdateQuestion,
  onToggleQuestionApproval,
  onDeleteQuestion,
  onRestoreQuestion,
  onUpdatePreliminary,
  onTogglePreliminaryApproval,
  onUpdateAIPolicy,
  onNavigateToRoleAccess,
}) => {
  const [selectedServiceId, setSelectedServiceId] = useState<string>(
    initialSelectedServiceId || (services[0]?.service_id ?? 'FIND_SCIENTIFIC_MATERIALS')
  );
  const [selectedStage, setSelectedStage] = useState<number>(2);
  const [selectedBlock, setSelectedBlock] = useState<'A' | 'B' | 'C'>('A');
  const [filterReviewStatus, setFilterReviewStatus] = useState<string>('ALL');
  const [serviceSearch, setServiceSearch] = useState('');

  const [editingQuestion, setEditingQuestion] = useState<CatalogQuestion | null>(null);
  const [editingPreliminary, setEditingPreliminary] = useState<CatalogPreliminary | null>(null);

  const selectedService = services.find((s) => s.service_id === selectedServiceId) || services[0];

  const serviceQuestions = questions.filter((q) => q.service_id === selectedServiceId);
  const stageQuestions = serviceQuestions.filter((q) => {
    if (q.stage !== selectedStage) return false;
    if (selectedStage === 2 && q.block !== selectedBlock) return false;
    if (filterReviewStatus !== 'ALL' && q.status !== filterReviewStatus) return false;
    return true;
  });

  const getPreliminaryForQuestion = (qId: string): CatalogPreliminary | undefined =>
    preliminaries.find((p) => p.service_id === selectedServiceId && p.question_id === qId);

  const approvedQuestionsCount = serviceQuestions.filter((q) => q.status === 'APPROVED').length;
  const progressPercent = serviceQuestions.length > 0 ? Math.round((approvedQuestionsCount / serviceQuestions.length) * 100) : 0;

  const filteredServices = services.filter((s) => {
    if (!serviceSearch.trim()) return true;
    const q = serviceSearch.toLowerCase();
    return s.title.toLowerCase().includes(q) || s.display_code.toLowerCase().includes(q) || s.service_id.toLowerCase().includes(q);
  });

  return (
    <div className="coverage-main-layout">
      <aside className="coverage-role-sidebar">
        <div className="cov-sidebar-heading">Индекс услуг (11×72)</div>
        <input
          type="text"
          className="cov-search-input"
          style={{ minWidth: 'auto', width: '100%', marginBottom: '0.5rem', padding: '0.35rem 0.5rem' }}
          placeholder="Фильтр услуг..."
          value={serviceSearch}
          onChange={(e) => setServiceSearch(e.target.value)}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', maxHeight: '70vh', overflowY: 'auto' }}>
          {directions.map((dir) => {
            const dirSrvs = filteredServices.filter((s) => s.primary_direction_id === dir.direction_id);
            if (dirSrvs.length === 0) return null;

            return (
              <div key={dir.direction_id} style={{ marginBottom: '0.5rem' }}>
                <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: '#38bdf8', padding: '0.2rem 0.5rem' }}>
                  {dir.display_code}. {dir.title}
                </div>
                {dirSrvs.map((s) => {
                  const isSelected = s.service_id === selectedServiceId;
                  return (
                    <button
                      key={s.service_id}
                      className={`cov-role-item ${isSelected ? 'active' : ''}`}
                      style={{ padding: '0.4rem 0.5rem', fontSize: '0.75rem' }}
                      onClick={() => setSelectedServiceId(s.service_id)}
                    >
                      <span style={{ color: '#f59e0b', fontWeight: 600, marginRight: '0.4rem' }}>{s.display_code}</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </aside>

      <div className="coverage-content-panel">
        {selectedService && (
          <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '12px', padding: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                  <span className="cov-dir-code" style={{ fontSize: '1rem' }}>{selectedService.display_code}</span>
                  <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#ffffff' }}>{selectedService.title}</h2>
                  <span className="cov-role-badge cov-badge-allowed">Активна</span>
                </div>
                <div style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>
                  Код: <strong>{selectedService.display_code}</strong> · Маршрут: <strong>{selectedService.route}</strong>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button
                  className="cov-btn cov-btn-outline"
                  onClick={() => onNavigateToRoleAccess(selectedService.service_id)}
                  title="Перейти к настройке ролей для этой услуги"
                >
                  ← Доступы по ролям
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: '#131d31', padding: '0.75rem 1rem', borderRadius: '8px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>
                  <span>Прогресс готовности (утверждено вопросов)</span>
                  <strong style={{ color: '#fff' }}>{approvedQuestionsCount} / {serviceQuestions.length} ({progressPercent}%)</strong>
                </div>
                <div style={{ width: '100%', height: '6px', background: '#1e293b', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ width: `${progressPercent}%`, height: '100%', background: '#10b981', transition: 'width 0.3s' }} />
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '12px', padding: '1rem' }}>
          <div className="cov-stage-nav">
            {[0, 1, 2, 3, 4, 5].map((st) => {
              const labels = [
                'Этап 0 · Способ начала',
                'Этап 1 · Направление и услуга',
                'Этап 2 · Сведения и материалы',
                'Этап 3 · Проверка ответов',
                'Этап 4 · Уточнения консультанта',
                'Этап 5 · Подтверждение и отправка',
              ];
              return (
                <button
                  key={st}
                  className={`cov-stage-tab-btn ${selectedStage === st ? 'active' : ''}`}
                  onClick={() => setSelectedStage(st)}
                >
                  {labels[st]}
                </button>
              );
            })}
          </div>

          {selectedStage === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ background: '#131d31', padding: '1rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9375rem', color: '#38bdf8' }}>
                  Политика Этапа 0: «Способ начала»
                </h4>
                <p style={{ margin: 0, fontSize: '0.8125rem', color: '#cbd5e1' }}>
                  Этап 0 является управленческим экраном. Отображается исключительно пользователям с ролями <strong>владелец системы</strong> и <strong>операционный партнёр</strong>. Все остальные роли (клиент, ментор, автор и т.д.) начинают сразу с этапа 1.
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                <div style={{ background: '#131d31', border: '1px solid #334155', borderRadius: '8px', padding: '1.25rem' }}>
                  <div style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>+</div>
                  <h4 style={{ margin: '0 0 0.25rem 0', color: '#fff' }}>Новая заявка с нуля</h4>
                  <p style={{ margin: 0, fontSize: '0.8125rem', color: '#94a3b8' }}>
                    Создание отдельного драфта без подтягивания клиентских ответов.
                  </p>
                </div>

                <div style={{ background: '#131d31', border: '1px solid #334155', borderRadius: '8px', padding: '1.25rem' }}>
                  <div style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>↓</div>
                  <h4 style={{ margin: '0 0 0.25rem 0', color: '#fff' }}>Потянуть ответы клиента</h4>
                  <p style={{ margin: 0, fontSize: '0.8125rem', color: '#94a3b8' }}>
                    Импорт ответов клиента по REQ-коду с автоматической фиксацией источника (provenance).
                  </p>
                </div>
              </div>
            </div>
          )}

          {selectedStage === 1 && (
            <div style={{ background: '#131d31', padding: '1rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9375rem', color: '#38bdf8' }}>
                Конфигурация Этапа 1: «Направление и услуга»
              </h4>
              <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.8125rem', color: '#cbd5e1' }}>
                Пользователи видят только опубликованные услуги, доступные их роли.
              </p>
              <div style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>
                Выбранная услуга: <strong style={{ color: '#fff' }}>{selectedService?.display_code} {selectedService?.title}</strong>
              </div>
            </div>
          )}

          {selectedStage === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {(['A', 'B', 'C'] as const).map((blk) => {
                    const blockNames = {
                      A: 'Блок A: Задача и контекст',
                      B: 'Блок B: Требования к результату',
                      C: 'Блок C: Материалы и файлы',
                    };
                    return (
                      <button
                        key={blk}
                        className={`cov-tab-btn ${selectedBlock === blk ? 'active' : ''}`}
                        onClick={() => setSelectedBlock(blk)}
                      >
                        {blockNames[blk]}
                      </button>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Статус проверки:</span>
                  <select
                    className="cov-form-select"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    value={filterReviewStatus}
                    onChange={(e) => setFilterReviewStatus(e.target.value)}
                  >
                    <option value="ALL">Все статусы</option>
                    <option value="APPROVED">Утверждённые</option>
                    <option value="CHANGED">Изменённые</option>
                    <option value="NOT_REVIEWED">Не проверенные</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {stageQuestions.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8', fontSize: '0.875rem' }}>
                    В выбранном блоке нет вопросов, удовлетворяющих фильтру.
                  </div>
                ) : (
                  stageQuestions.map((q) => {
                    const prelim = getPreliminaryForQuestion(q.question_id);
                    const isApproved = q.status === 'APPROVED';

                    return (
                      <div key={q.question_id} className="cov-question-card">
                        <div className="cov-question-head">
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
                              <span className="cov-q-label">{q.label}</span>
                              {q.required && <span style={{ color: '#ef4444', fontWeight: 700 }}>*</span>}
                              <span className={`cov-role-badge ${isApproved ? 'cov-badge-allowed' : q.status === 'CHANGED' ? 'cov-badge-partial' : 'cov-badge-denied'}`}>
                                {{ APPROVED: 'Утверждён', CHANGED: 'Изменён', DRAFT: 'Черновик', NEW: 'Новый', DELETED: 'Удалён', ERROR: 'Нужно исправить', NOT_REVIEWED: 'Ожидает проверки' }[q.status] || 'Ожидает проверки'}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.6875rem', color: '#64748b' }}>
                              {q.help_text || ''}
                            </div>
                          </div>

                          <div className="cov-q-meta">
                            <button
                              className="cov-btn cov-btn-outline"
                              style={{ fontSize: '0.75rem', padding: '0.3rem 0.5rem' }}
                              onClick={() => setEditingQuestion(q)}
                            >
                              Редактировать
                            </button>

                            <button
                              className={`cov-btn ${isApproved ? 'cov-btn-outline' : 'cov-btn-success'}`}
                              style={{ fontSize: '0.75rem', padding: '0.3rem 0.5rem' }}
                              onClick={() => onToggleQuestionApproval(q.question_id, q.status)}
                            >
                              {isApproved ? 'Снять ✓' : 'Утвердить ✓'}
                            </button>

                            {q.is_deleted ? (
                              <button
                                className="cov-btn cov-btn-outline"
                                style={{ fontSize: '0.75rem', padding: '0.3rem 0.5rem' }}
                                onClick={() => onRestoreQuestion(q.question_id)}
                              >
                                Восстановить
                              </button>
                            ) : (
                              <button
                                className="cov-btn cov-btn-danger-outline"
                                style={{ fontSize: '0.75rem', padding: '0.3rem 0.5rem' }}
                                onClick={() => onDeleteQuestion(q.question_id)}
                              >
                                Удалить
                              </button>
                            )}
                          </div>
                        </div>

                        {prelim && (
                          <div className="cov-prelim-box">
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
                                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#f59e0b' }}>Предварительный ответ:</span>
                                <span className="cov-role-badge cov-badge-partial">{{ APPROVED: 'Утверждён', DRAFT: 'Черновик', CHANGED: 'Изменён', NEW: 'Новый' }[prelim.status] || 'Ожидает проверки'}</span>
                                {prelim.confidence != null && (
                                  <span style={{ fontSize: '0.6875rem', color: '#38bdf8' }}>
                                    {(prelim.confidence * 100).toFixed(0)}% уверенности
                                  </span>
                                )}
                              </div>
                              <div className="cov-prelim-val">
                                {prelim.value_template ? `«${prelim.value_template}»` : '[Пустой шаблон — ожидает ввода заявителя]'}
                              </div>
                            </div>

                            <div style={{ display: 'flex', gap: '0.35rem' }}>
                              <button
                                className="cov-btn cov-btn-outline"
                                style={{ fontSize: '0.6875rem', padding: '0.25rem 0.4rem' }}
                                onClick={() => setEditingPreliminary(prelim)}
                              >
                                Редактировать ответ
                              </button>
                              <button
                                className={`cov-btn ${prelim.status_review === 'APPROVED' ? 'cov-btn-outline' : 'cov-btn-success'}`}
                                style={{ fontSize: '0.6875rem', padding: '0.25rem 0.4rem' }}
                                onClick={() => onTogglePreliminaryApproval(prelim.question_id, prelim.status_review)}
                              >
                                {prelim.status_review === 'APPROVED' ? 'Снять ✓' : 'Утвердить ✓'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {selectedStage === 3 && (
            <div style={{ background: '#131d31', padding: '1rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9375rem', color: '#38bdf8' }}>
                Этап 3: «Проверка ответов» (Deterministic Review)
              </h4>
              <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.8125rem', color: '#cbd5e1' }}>
                На Этапе 3 система проверяет ответы пользователя по правилам. Консультант на этом шаге не вызывается, чтобы исключить случайные блокировки.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.8125rem', color: '#94a3b8' }}>
                <div>✓ Проверка заполненности обязательных полей.</div>
                <div>✓ Валидация форматов файлов и лимитов размера.</div>
                <div>✓ Запрос подтверждения заявителя для предварительно подтянутых данных.</div>
              </div>
            </div>
          )}

          {selectedStage === 4 && (
            <div style={{ background: '#131d31', padding: '1.25rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: '1rem', color: '#38bdf8' }}>
                    Этап 4: «Уточнения и рекомендации»
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>
                    Единственное место заявочного маршрута, где консультант формирует контекстные уточнения
                  </p>
                </div>
                <span className="cov-role-badge cov-badge-info">Только на этом этапе</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div className="cov-form-group">
                  <label className="cov-form-label">Максимум раундов уточнений</label>
                  <input
                    type="number"
                    className="cov-form-input"
                    value={aiPolicy.max_rounds}
                    onChange={(e) => onUpdateAIPolicy({ ...aiPolicy, max_rounds: Number(e.target.value) })}
                  />
                </div>

                <div className="cov-form-group">
                  <label className="cov-form-label">Стратегия при отказе / таймауте консультанта</label>
                  <select
                    className="cov-form-select"
                    value={aiPolicy.fallback_strategy}
                    onChange={(e) => onUpdateAIPolicy({ ...aiPolicy, fallback_strategy: e.target.value as any })}
                  >
                    <option value="CONTINUE_WITH_AUDIT">Продолжить без консультанта с записью в аудит</option>
                    <option value="DETERMINISTIC_BLOCK">Блокировать переход до успешной проверки</option>
                  </select>
                </div>
              </div>

              <div className="cov-form-group">
                <label className="cov-form-label">Критерии блокирующих уточнений</label>
                <textarea
                  className="cov-form-textarea"
                  rows={3}
                  value={aiPolicy.blocking_rules.join('\n')}
                  onChange={(e) =>
                    onUpdateAIPolicy({
                      ...aiPolicy,
                      blocking_rules: e.target.value.split('\n').filter(Boolean),
                    })
                  }
                  placeholder="Один критерий на строку..."
                />
              </div>

              <div style={{ marginTop: '0.75rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={aiPolicy.visible_explanation}
                    onChange={(e) => onUpdateAIPolicy({ ...aiPolicy, visible_explanation: e.target.checked })}
                  />
                  Показывать пользователю объяснение, почему AI задал данный вопрос
                </label>
              </div>
            </div>
          )}

          {selectedStage === 5 && (
            <div style={{ background: '#131d31', padding: '1rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9375rem', color: '#38bdf8' }}>
                Этап 5: «Подтверждение и отправка» (Final Human Confirmation)
              </h4>
              <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.8125rem', color: '#cbd5e1' }}>
                Финальная отправка требует обязательного подтверждения уполномоченным лицом. Консультант никогда не отправляет заявку автоматически.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.8125rem', color: '#94a3b8' }}>
                <div>✓ Генерация неизменяемого слепка заявки (snapshot).</div>
                <div>✓ Защита от повторной отправки (idempotency key).</div>
                <div>✓ Заявка получает статус «NEW» / «Ожидает рассмотрения».</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <QuestionEditDrawer
        question={editingQuestion}
        isOpen={!!editingQuestion}
        onClose={() => setEditingQuestion(null)}
        onSave={(updated) => {
          onUpdateQuestion(updated);
          setEditingQuestion(null);
        }}
      />

      <PreliminaryEditModal
        preliminary={editingPreliminary}
        isOpen={!!editingPreliminary}
        onClose={() => setEditingPreliminary(null)}
        onSave={(updated) => {
          onUpdatePreliminary(updated);
          setEditingPreliminary(null);
        }}
      />
    </div>
  );
};
